import * as React from "react";
import { Clock, Receipt, Money, Pause, X, Warning } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { formatMoney, formatSessionDuration, formatTimeShort, relativeMinutes } from "./utils";
import type { CashierLabels, CashierSession } from "./types";

export interface CashierSessionHeaderProps {
  session: CashierSession;
  labels: CashierLabels;
  onCloseSession?: () => void;
  parkedCount: number;
  onOpenParkedDrawer?: () => void;
  branchName?: string;
  locale?: "tr" | "en";
}

export function CashierSessionHeader({
  session, labels, onCloseSession, parkedCount, onOpenParkedDrawer, branchName, locale = "tr",
}: CashierSessionHeaderProps) {
  const minutes = relativeMinutes(session.openedAt);
  const longOpen = minutes > 4 * 60;
  const isClosed = session.status !== "open";
  return (
    <header className="flex flex-wrap items-center gap-3 px-4 md:px-6 py-3 bg-biz-surface-elevated border-b border-biz-border">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="h-10 w-10 rounded-biz-btn bg-biz-primary text-biz-primary-foreground flex items-center justify-center font-semibold shrink-0">
          {session.openedByName.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-biz-foreground truncate">{session.openedByName}</p>
            {branchName && (
              <span className="text-xs text-biz-foreground-subtle">· {branchName}</span>
            )}
            {isClosed && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-biz-muted text-[10px] font-semibold text-biz-foreground-muted">
                <X className="h-3 w-3" />
                {labels.states.sessionClosedTitle}
              </span>
            )}
          </div>
          <p className="text-xs text-biz-foreground-muted flex items-center gap-1.5 flex-wrap">
            <Clock className="h-3 w-3" />
            <span>{labels.session.openedAt} {formatTimeShort(session.openedAt, locale)}</span>
            <span className="text-biz-foreground-subtle">·</span>
            <span className={cn("font-mono-num tabular-nums", longOpen && "text-biz-warning font-semibold")}>
              {formatSessionDuration(minutes, locale)}
            </span>
            {longOpen && (
              <span className="inline-flex items-center gap-1 text-biz-warning">
                <Warning weight="fill" className="h-3 w-3" />
                {labels.session.warnLongOpen}
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 md:gap-4">
        <Stat icon={<Receipt className="h-4 w-4" />} label={labels.session.ordersCount} value={String(session.ordersCount)} />
        <Stat icon={<Money className="h-4 w-4" />} label={labels.session.gross} value={formatMoney(session.grossMinor, labels.currencyPrefix, locale)} />
        <Stat label={labels.session.cash} value={formatMoney(session.cashCollectedMinor, labels.currencyPrefix, locale)} muted />
      </div>

      <div className="flex items-center gap-2">
        {parkedCount > 0 && (
          <button
            type="button"
            onClick={onOpenParkedDrawer}
            className={cn(
              "h-11 px-3 rounded-biz-btn text-sm font-semibold inline-flex items-center gap-2",
              "border border-biz-accent/40 bg-biz-accent-subtle text-biz-accent-foreground",
              "hover:bg-biz-accent/20 transition-colors biz-focus-ring",
            )}
          >
            <Pause className="h-4 w-4" />
            <span>{labels.session.parkedCount}</span>
            <span className="font-mono-num tabular-nums px-1.5 py-0.5 rounded-full bg-biz-accent text-biz-accent-foreground text-xs">
              {parkedCount}
            </span>
          </button>
        )}
        <button
          type="button"
          onClick={onCloseSession}
          disabled={isClosed}
          className={cn(
            "h-11 px-3 rounded-biz-btn text-sm font-semibold",
            "border border-biz-border text-biz-foreground-muted hover:text-biz-danger hover:bg-biz-danger-subtle hover:border-biz-danger-border",
            "transition-colors biz-focus-ring disabled:opacity-50 disabled:cursor-not-allowed",
          )}
        >
          {labels.session.closeSession}
        </button>
      </div>
    </header>
  );
}

function Stat({ icon, label, value, muted }: { icon?: React.ReactNode; label: string; value: string; muted?: boolean }) {
  return (
    <div className={cn("hidden md:flex flex-col items-end leading-tight", muted && "opacity-70")}>
      <span className="text-[10px] uppercase tracking-wider text-biz-foreground-subtle flex items-center gap-1">
        {icon}{label}
      </span>
      <span className="font-mono-num text-sm font-bold text-biz-foreground tabular-nums">{value}</span>
    </div>
  );
}
